import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ExpandedState } from "@tanstack/react-table";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { getDisplayedEntityIds } from "@common/types/domains/accounting/chartOfAccountsViewFilters";
import { ViewDensity, ViewType } from "@common/types/views/views";
import {
    ChartOfAccountsColumn,
    ChartOfAccountsView,
    ChartOfAccountsViewSchema,
} from "@common/types/views/chartOfAccountsView";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import {
    useWorkspaceUserCache,
    WORKSPACE_USER_CACHE_KEYS,
} from "@hooks/useWorkspaceUserCache";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useChartOfAccountsQuery } from "@api/accountingReports.api";
import { ChartOfAccountsOrderMode } from "@common/helpers/domains/accounting/buildHierarchicalChartOfAccounts";
import { useAccountIdsWithEnabledRules } from "@hooks/useAccountIdsWithEnabledRules";
import { buildCoaGroupFilterOptionsFromChartAccounts } from "@common/helpers/domains/accounting/coaGroupFilter";
import { Button } from "@design-system/Button/Button";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { useAccountingTabContext } from "../useAccountingTab";
import { useEntitiesWithAccountingAvailable } from "../useEntitiesWithAccountingAvailable";
import { ChartOfAccountsExport } from "./ChartOfAccountsExport";
import { CoaAccountMappingModal } from "./CoaAccountMapping/CoaAccountMappingModal";
import { CreateAccountSpreadsheetModal } from "./CreateAccountSpreadsheet/CreateAccountSpreadsheetModal";
import styles from "./styles.module.scss";
import {
    buildHierarchicalChartOfAccounts,
    getDefaultExpandedState,
} from "./utils";
import { createChartOfAccountsViewConfig } from "./chartOfAccountsViewConfig";
import {
    buildChartOfAccountsBuiltInView,
    ChartOfAccountsBuiltInViewId,
} from "./chartOfAccountsBuiltInViews";
import { EditChartOfAccountsButton } from "./EditChartOfAccountsButton";
import { ChartOfAccountsViewToolbar } from "./ChartOfAccountsViewToolbar";
import { WorkspaceChartEditorDrawer } from "@components/settings/ChartOfAccountsTemplates/editor/workspaceHost/WorkspaceChartEditorDrawer";
import { EditorFocusTarget } from "@components/settings/ChartOfAccountsTemplates/editor/editorBodyTypes";
import { useCoaFiltersCache } from "./useCoaFiltersCache";
import { MEMBER_RESTRICTION_TOOLTIP } from "./constants";

interface ChartOfAccountsTableCallbacks {
    openEditorForRow: (target: EditorFocusTarget) => void;
}

export type ChartOfAccountsTableMeta = ViewTableMeta<
    ChartOfAccountsTableCallbacks,
    unknown
>;

interface ChartOfAccountsPageProps {
    /**
     * Drawer use: starts on the given built-in view, keeps the entity
     * selection fixed, and leaves the page's cached filters and view alone.
     */
    embedded?: { initialViewId: ChartOfAccountsBuiltInViewId };
}

export const ChartOfAccountsPage: React.FC<ChartOfAccountsPageProps> = ({
    embedded,
}) => {
    const isEmbedded = embedded !== undefined;
    const entities = useEntitiesWithAccountingAvailable();
    const { filters, setFilters, selectedEntity } = useAccountingTabContext();
    const { bulkCoaUpload, coaAccountMapping } = useTypedFlags();

    const canEditCoA = useCanPerformWorkspaceAction(WorkspaceAction.COA_EDIT);
    // View-only keeps a live button and gets told why; every other role that
    // cannot edit the chart of accounts keeps today's disabled button and its
    // tooltip.
    const { isViewOnly, refuseIfViewOnly } = useViewOnlyRefusal();
    const isCoaEditDisabled = !canEditCoA && !isViewOnly;
    const guardCoaEdit = (action: () => void) => () => {
        if (refuseIfViewOnly()) {
            return;
        }
        action();
    };

    const showAccountNumbers = useShowAccountNumbers();

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_CHART_OF_ACCOUNTS_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    // Status/source/subtype/group otherwise live only in the URL (shared
    // AccountingTabContext), so they reset whenever a user navigates away
    // and back. Cache them per-workspace-user and restore them once on
    // mount when the URL doesn't already specify them (e.g. a shared link).
    // Search is session-only and is not cached.
    const [cachedCoaFilters, setCachedCoaFilters] = useCoaFiltersCache();
    const [isCoaFiltersHydrated, setIsCoaFiltersHydrated] = useState(false);

    useEffect(() => {
        if (isCoaFiltersHydrated) {
            return;
        }
        if (isEmbedded) {
            setIsCoaFiltersHydrated(true);
            return;
        }
        const hasFilterInUrl =
            filters.accountStatus !== undefined ||
            filters.accountSource !== undefined ||
            filters.accountRolePresence !== undefined ||
            filters.accountRulePresence !== undefined ||
            (filters.accountSubtypes?.length ?? 0) > 0 ||
            (filters.form1099Boxes?.length ?? 0) > 0 ||
            (filters.coaTemplateGroupKeys?.length ?? 0) > 0;
        if (!hasFilterInUrl && cachedCoaFilters) {
            setFilters((current) => ({
                ...current,
                accountStatus: cachedCoaFilters.accountStatus,
                accountSource: cachedCoaFilters.accountSource,
                accountRolePresence: cachedCoaFilters.accountRolePresence,
                accountRulePresence: cachedCoaFilters.accountRulePresence,
                accountSubtypes: cachedCoaFilters.accountSubtypes,
                form1099Boxes: cachedCoaFilters.form1099Boxes,
                coaTemplateGroupKeys: cachedCoaFilters.coaTemplateGroupKeys,
            }));
        }
        setIsCoaFiltersHydrated(true);
    }, [
        isEmbedded,
        isCoaFiltersHydrated,
        cachedCoaFilters,
        filters.accountStatus,
        filters.accountSource,
        filters.accountRolePresence,
        filters.accountRulePresence,
        filters.accountSubtypes,
        filters.form1099Boxes,
        filters.coaTemplateGroupKeys,
        setFilters,
    ]);

    useEffect(() => {
        if (!isCoaFiltersHydrated || isEmbedded) {
            return;
        }
        setCachedCoaFilters({
            accountStatus: filters.accountStatus,
            accountSource: filters.accountSource,
            accountRolePresence: filters.accountRolePresence,
            accountRulePresence: filters.accountRulePresence,
            accountSubtypes: filters.accountSubtypes,
            form1099Boxes: filters.form1099Boxes,
            coaTemplateGroupKeys: filters.coaTemplateGroupKeys,
        });
    }, [
        isEmbedded,
        isCoaFiltersHydrated,
        filters.accountStatus,
        filters.accountSource,
        filters.accountRolePresence,
        filters.accountRulePresence,
        filters.accountSubtypes,
        filters.form1099Boxes,
        filters.coaTemplateGroupKeys,
        setCachedCoaFilters,
    ]);

    const { data, isLoading } = useChartOfAccountsQuery();
    const { accountIdsWithEnabledRules, isLoading: isRulesLoading } =
        useAccountIdsWithEnabledRules({
            accounts: data?.accounts,
            enabled: true,
        });
    const displayedEntityIds = useMemo(
        () => getDisplayedEntityIds(filters),
        [filters],
    );
    const hierarchicalData = useMemo(
        () =>
            buildHierarchicalChartOfAccounts({
                accounts: data?.accounts,
                groups: data?.groups,
                filters: {
                    ...filters,
                    entityIds: displayedEntityIds,
                },
                accountIdsWithEnabledRules,
                orderMode: ChartOfAccountsOrderMode.sortOrder,
                showAccountNumbers,
                withPredefinedSections: true,
            }),
        [
            data,
            filters,
            displayedEntityIds,
            accountIdsWithEnabledRules,
            showAccountNumbers,
        ],
    );
    const isRulePresenceFilterLoading =
        filters.accountRulePresence !== undefined &&
        accountIdsWithEnabledRules === undefined &&
        isRulesLoading;

    const groupFilterOptions = useMemo(
        () =>
            buildCoaGroupFilterOptionsFromChartAccounts({
                accounts: data?.accounts ?? [],
                groups: data?.groups ?? [],
                displayedEntityIds,
            }),
        [data?.accounts, data?.groups, displayedEntityIds],
    );

    const [showCreateAccountSpreadsheet, setShowCreateAccountSpreadsheet] =
        useState(false);
    const [showCoaAccountMapping, setShowCoaAccountMapping] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editorFocusTarget, setEditorFocusTarget] =
        useState<EditorFocusTarget | null>(null);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const tableRef = useRef<ViewTableHandle>(null);
    const hasInitializedExpandedRef = useRef(false);

    useEffect(() => {
        if (
            hasInitializedExpandedRef.current ||
            !hierarchicalData ||
            hierarchicalData.length === 0
        ) {
            return;
        }
        hasInitializedExpandedRef.current = true;
        setExpanded(getDefaultExpandedState(hierarchicalData));
    }, [hierarchicalData]);

    const openEditorForRow = useCallback((target: EditorFocusTarget) => {
        setEditorFocusTarget(target);
        setIsEditorOpen(true);
    }, []);

    const openEditorWithoutTarget = useCallback(() => {
        setEditorFocusTarget(null);
        setIsEditorOpen(true);
    }, []);

    const closeEditor = useCallback(() => {
        setIsEditorOpen(false);
        setEditorFocusTarget(null);
    }, []);

    // So a later reseed does not re-focus the original Edit row.
    const handleFocusTargetConsumed = useCallback(() => {
        setEditorFocusTarget(null);
    }, []);

    const viewConfig = useMemo(() => {
        const config = createChartOfAccountsViewConfig();
        return {
            ...config,
            columns: {
                ...config.columns,
                [ChartOfAccountsColumn.SUBTYPE]: {
                    ...config.columns[ChartOfAccountsColumn.SUBTYPE],
                    hiddenFromSettings: true,
                },
            },
        };
    }, []);

    const [cachedView, setCachedView] =
        useWorkspaceUserCache<ChartOfAccountsView>(
            WORKSPACE_USER_CACHE_KEYS.CHART_OF_ACCOUNTS_PAGE_VIEW_CACHE_KEY,
            ChartOfAccountsViewSchema,
        );

    const defaultView: ChartOfAccountsView = useMemo(
        () => ({
            type: ViewType.CHART_OF_ACCOUNTS,
            filters: {},
            columns: [
                { id: ChartOfAccountsColumn.NAME, options: { pin: "left" } },
                { id: ChartOfAccountsColumn.SOURCE },
                {
                    id: ChartOfAccountsColumn.ENTITIES,
                    options: { pin: "right" },
                },
                {
                    id: ChartOfAccountsColumn.ACTIONS,
                    options: { pin: "right" },
                },
            ],
            options: {
                density: ViewDensity.LG,
            },
        }),
        [],
    );

    const [view, setView] = useState<ChartOfAccountsView>(() =>
        embedded
            ? buildChartOfAccountsBuiltInView({
                  defaultView,
                  viewId: embedded.initialViewId,
              })
            : (cachedView ?? defaultView),
    );

    const effectiveView = useMemo(
        () => ({
            ...view,
            columns: view.columns.filter(
                (column) => column.id !== ChartOfAccountsColumn.SUBTYPE,
            ),
        }),
        [view],
    );

    const handleViewChange = useCallback(
        (newView: ChartOfAccountsView) => {
            setView(newView);
            if (!isEmbedded) {
                setCachedView(newView);
            }
        },
        [isEmbedded, setCachedView],
    );

    const tableMeta: ChartOfAccountsTableMeta = useMemo(
        () => ({
            callbacks: { openEditorForRow },
        }),
        [openEditorForRow],
    );

    const handleShowCreateAccountSpreadsheet = useCallback(() => {
        setShowCreateAccountSpreadsheet(true);
    }, []);

    const handleHideCreateAccountSpreadsheet = useCallback(() => {
        setShowCreateAccountSpreadsheet(false);
    }, []);

    const handleShowCoaAccountMapping = useCallback(() => {
        setShowCoaAccountMapping(true);
    }, []);

    const handleHideCoaAccountMapping = useCallback(() => {
        setShowCoaAccountMapping(false);
    }, []);

    const renderAddAccountsButton = () => {
        if (isEmbedded && !selectedEntity) {
            return null;
        }

        if (coaAccountMapping) {
            return (
                <Tooltip
                    disabled={canEditCoA || isViewOnly}
                    content={MEMBER_RESTRICTION_TOOLTIP}
                >
                    <Button
                        variant="primary"
                        size="md"
                        disabled={isCoaEditDisabled}
                        onClick={guardCoaEdit(handleShowCoaAccountMapping)}
                    >
                        Import accounts
                    </Button>
                </Tooltip>
            );
        }

        if (bulkCoaUpload) {
            return (
                <Tooltip
                    disabled={canEditCoA || isViewOnly}
                    content={MEMBER_RESTRICTION_TOOLTIP}
                >
                    <Button
                        variant="primary"
                        size="md"
                        disabled={isCoaEditDisabled}
                        onClick={guardCoaEdit(
                            handleShowCreateAccountSpreadsheet,
                        )}
                    >
                        Add accounts
                    </Button>
                </Tooltip>
            );
        }

        return null;
    };

    return (
        <>
            <ChartOfAccountsViewToolbar
                view={effectiveView}
                defaultView={defaultView}
                onViewChange={handleViewChange}
                viewConfig={viewConfig}
                filters={filters}
                setFilters={setFilters}
                initialBaseViewId={embedded?.initialViewId}
            />
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={setFilters}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                requireDateSelection
                hideCreateJournalEntryButton
                hideDateRangeFilter
                hideClassFilter
                hideEntityFilter
                showAccountEnabledFilter
                showAccountSourceFilter
                showAccountRolePresenceFilter
                showAccountRulePresenceFilter
                showForm1099BoxFilter
                showGroupFilter
                staticTemplateGroupOptions={groupFilterOptions}
                hideLedgerSelect
                additionalButtons={
                    <>
                        <CollapseAllButton
                            expanded={expanded}
                            tableRef={tableRef}
                        />
                        {!isEmbedded && (
                            <EntityFilterCombobox
                                filters={{ entityIds: displayedEntityIds }}
                                onlyBusiness
                                onChange={(newFilters) =>
                                    setFilters((currentFilters) => ({
                                        ...currentFilters,
                                        entityIds: newFilters.entityIds?.length
                                            ? newFilters.entityIds
                                            : entities.map(
                                                  (entity) => entity.id,
                                              ),
                                    }))
                                }
                                excludeAllMockEntities
                            />
                        )}
                        {renderAddAccountsButton()}
                        <EditChartOfAccountsButton
                            canEditCoA={canEditCoA}
                            onOpen={openEditorWithoutTarget}
                        />
                        <ChartOfAccountsExport
                            filters={filters}
                            columns={effectiveView.columns.map(
                                (column) => column.id,
                            )}
                        />
                    </>
                }
            />
            <div data-testid="chart-of-accounts-table">
                <ViewTable
                    ref={tableRef}
                    data={
                        isLoading || isRulePresenceFilterLoading
                            ? null
                            : hierarchicalData
                    }
                    view={effectiveView}
                    config={viewConfig}
                    className={styles.report}
                    tableMeta={tableMeta}
                    fixedTopOffsetPx
                    onViewChange={(newView) =>
                        handleViewChange(newView as ChartOfAccountsView)
                    }
                    loadingView={() => <Loader />}
                    emptyView={() => (
                        <EmptyState
                            header="No accounts for the selected filters"
                            ctaText="Clear filters"
                            action={() => {
                                setFilters((current) => ({
                                    ...current,
                                    search: undefined,
                                    accountStatus: undefined,
                                    accountSource: undefined,
                                    accountRolePresence: undefined,
                                    accountRulePresence: undefined,
                                    accountSubtypes: undefined,
                                    form1099Boxes: undefined,
                                    coaTemplateGroupKeys: undefined,
                                }));
                            }}
                        />
                    )}
                    autoExpandOnMount
                    expanded={expanded}
                    onExpandedChange={setExpanded}
                    rowBorders={false}
                    nestingIndentPx={16}
                />
            </div>

            <CreateAccountSpreadsheetModal
                show={showCreateAccountSpreadsheet}
                onHide={handleHideCreateAccountSpreadsheet}
            />

            <CoaAccountMappingModal
                show={showCoaAccountMapping}
                onHide={handleHideCoaAccountMapping}
            />

            <WorkspaceChartEditorDrawer
                isOpen={isEditorOpen}
                onClose={closeEditor}
                focusTarget={editorFocusTarget}
                onFocusTargetConsumed={handleFocusTargetConsumed}
            />
        </>
    );
};
